// How each call connection is doing, from the browser's WebRTC statistics:
// round trip, packet loss, jitter, how fast data comes in and goes out, and
// the picture size. Sent to the server every 10 seconds while in a call for
// the admin dashboard. Nothing about the audio or video itself is sent.

export const REPORT_MS = 10000;

const read = async (pc) => {
    const stats = await pc.getStats();
    const out = { bytesIn: 0, bytesOut: 0, lost: 0, received: 0, jitter: null, height: null, fps: null, rtt: null, relay: false, at: performance.now() };
    let pair = null;
    stats.forEach((r) => {
        if (r.type === 'transport' && r.selectedCandidatePairId) pair = stats.get(r.selectedCandidatePairId);
    });
    if (!pair) {
        // Firefox has no transport report; it marks the pair as selected.
        stats.forEach((r) => { if (r.type === 'candidate-pair' && (r.selected || r.nominated) && r.state === 'succeeded') pair = pair || r; });
    }
    if (pair) {
        if (pair.currentRoundTripTime != null) out.rtt = pair.currentRoundTripTime * 1000;
        const local = stats.get(pair.localCandidateId);
        const remote = stats.get(pair.remoteCandidateId);
        out.relay = local?.candidateType === 'relay' || remote?.candidateType === 'relay';
    }
    stats.forEach((r) => {
        if (r.type === 'inbound-rtp') {
            out.bytesIn += r.bytesReceived || 0;
            out.lost += Math.max(0, r.packetsLost || 0);
            out.received += r.packetsReceived || 0;
            if (r.kind === 'audio' && r.jitter != null) out.jitter = r.jitter * 1000;
            if (r.kind === 'video') {
                if (r.frameHeight) out.height = r.frameHeight;
                if (r.framesPerSecond != null) out.fps = r.framesPerSecond;
            }
        } else if (r.type === 'outbound-rtp') {
            out.bytesOut += r.bytesSent || 0;
        } else if (r.type === 'remote-inbound-rtp' && out.rtt == null && r.roundTripTime != null) {
            out.rtt = r.roundTripTime * 1000;
        }
    });
    return out;
};

/**
 * One connection's figures since the last report. `peer.lastStats` keeps the
 * previous reading, so rates and loss cover just the last stretch.
 */
export const measure = async (peer) => {
    if (!peer.pc || peer.pc.connectionState !== 'connected') return null;
    const now = await read(peer.pc);
    const prev = peer.lastStats;
    peer.lastStats = now;
    if (!prev) return null;
    const secs = (now.at - prev.at) / 1000;
    if (secs <= 0) return null;
    const lost = Math.max(0, now.lost - prev.lost);
    const got = Math.max(0, now.received - prev.received);
    const round = (v) => (v == null ? null : Math.round(v * 10) / 10);
    return {
        peer: peer.userId,
        relay: now.relay,
        rtt: round(now.rtt),
        loss: lost + got > 0 ? round((lost / (lost + got)) * 100) : null,
        jitter: round(now.jitter),
        kbpsIn: Math.round(((now.bytesIn - prev.bytesIn) * 8) / secs / 1000),
        kbpsOut: Math.round(((now.bytesOut - prev.bytesOut) * 8) / secs / 1000),
        height: now.height,
        fps: round(now.fps),
    };
};
